import React, { useState } from 'react';
import { X, Smartphone, Plus, Trash2, Edit3, Check, RotateCcw, AlertCircle } from 'lucide-react';
import { defaultDeviceTypes } from '../data/initialData';

interface ManageDeviceTypesModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceTypes: string[];
  onSave: (types: string[]) => void;
}

export const ManageDeviceTypesModal: React.FC<ManageDeviceTypesModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <ManageDeviceTypesModalContent {...props} />;
};

const ManageDeviceTypesModalContent: React.FC<ManageDeviceTypesModalProps> = ({
  isOpen,
  onClose,
  deviceTypes,
  onSave,
}) => {
  const [types, setTypes] = useState<string[]>(deviceTypes || defaultDeviceTypes);
  const [newTypeName, setNewTypeName] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const showNotification = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(null), 3000);
    } else {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(null), 2500);
    }
  };

  const handleAddType = () => {
    const trimmed = newTypeName.trim();
    if (!trimmed) {
      showNotification('Lütfen geçerli bir cihaz türü adı girin.', true);
      return;
    }

    if (types.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      showNotification('Bu cihaz türü zaten listede mevcut.', true);
      return;
    }

    const updated = [...types, trimmed];
    setTypes(updated);
    onSave(updated);
    setNewTypeName('');
    showNotification(`"${trimmed}" başarıyla eklendi.`);
  };

  const handleDeleteType = (typeName: string) => {
    if (types.length <= 1) {
      showNotification('En az bir cihaz türü kalmalıdır.', true);
      return;
    }

    const updated = types.filter(t => t !== typeName);
    setTypes(updated);
    onSave(updated);
    if (editingIndex !== null) {
      setEditingIndex(null);
    }
    showNotification(`"${typeName}" silindi.`);
  };

  const handleStartEdit = (index: number, currentText: string) => {
    setEditingIndex(index);
    setEditingText(currentText);
  };

  const handleSaveEdit = (index: number) => {
    const trimmed = editingText.trim();
    if (!trimmed) {
      showNotification('Cihaz türü adı boş olamaz.', true);
      return;
    }

    if (types.some((t, i) => i !== index && t.toLowerCase() === trimmed.toLowerCase())) {
      showNotification('Bu isimde başka bir cihaz türü zaten var.', true);
      return;
    }

    const updated = [...types];
    updated[index] = trimmed;
    setTypes(updated);
    onSave(updated);
    setEditingIndex(null);
    setEditingText('');
    showNotification('Cihaz türü güncellendi.');
  };

  const handleResetDefaults = () => {
    if (window.confirm('Tüm cihaz türleri varsayılan listeye sıfırlansın mı?')) {
      setTypes(defaultDeviceTypes);
      onSave(defaultDeviceTypes);
      showNotification('Varsayılan cihaz türleri geri yüklendi.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl sm:max-w-2xl border border-slate-200 overflow-hidden my-auto text-left transform transition-all">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white leading-tight">Cihaz Türlerini Yönet</h2>
              <p className="text-xs text-slate-400">Yeni cihaz türleri ekleyin, düzenleyin veya silin</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Notifications */}
        {errorMsg && (
          <div className="bg-rose-50 border-b border-rose-200 px-6 py-2.5 flex items-center gap-2 text-rose-700 text-xs font-semibold animate-in fade-in">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-2.5 flex items-center gap-2 text-emerald-700 text-xs font-semibold animate-in fade-in">
            <Check className="w-4 h-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        <div className="p-6 space-y-5">
          {/* Add New Type Input */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Yeni Cihaz Türü Ekle
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Cihaz türü adı yazın..."
                value={newTypeName}
                onChange={e => setNewTypeName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddType();
                  }
                }}
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              />
              <button
                type="button"
                onClick={handleAddType}
                disabled={!newTypeName.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer disabled:cursor-not-allowed shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Ekle</span>
              </button>
            </div>
          </div>

          {/* List of existing device types */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
              <span>Kayıtlı Cihaz Türleri ({types.length})</span>
              <button
                type="button"
                onClick={handleResetDefaults}
                className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-[11px] font-medium transition-colors cursor-pointer hover:underline"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Varsayılanları Yükle</span>
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-100 border border-slate-200 rounded-xl p-2 bg-white">
              {types.map((type, idx) => {
                const isEditing = editingIndex === idx;

                if (isEditing) {
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg"
                    >
                      <input
                        type="text"
                        autoFocus
                        value={editingText}
                        onChange={e => setEditingText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSaveEdit(idx);
                          } else if (e.key === 'Escape') {
                            setEditingIndex(null);
                          }
                        }}
                        className="flex-1 px-2.5 py-1 text-xs font-bold border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(idx)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded-md transition-colors cursor-pointer"
                        title="Kaydet"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingIndex(null)}
                        className="p-1.5 text-slate-400 hover:bg-slate-200 rounded-md transition-colors cursor-pointer"
                        title="İptal"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                }

                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{type}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(idx, type)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                        title="İsmi Düzenle"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteType(type)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                        title="Sil"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-3.5 flex justify-end border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold shadow transition-colors cursor-pointer"
          >
            Tamam / Kapat
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
